import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";

import axios from "../../../api/axios";
import { SETTLEMENTS_ENDPOINT } from "../../../consts";
import { formatCounterValue, formatNumber } from "../../../utils";
import { formatAxisNumber } from "../../ui/charts/chartFormatters";
import ChartCard from "../../ui/charts/chartCard.component";
import { buildChartSx, useChartPalette } from "../../ui/charts/chartPalette";

/** How many months of history the charts plot. Two years is enough to show
 * seasonality without crowding the axis. */
const CHART_MONTHS = 24;

interface MonthlySaleSummaryRow {
  month: string;
  total: string | number | null;
  black_pages_used: number | null;
  color_pages_used: number | null;
  scanned_pages_used: number | null;
  black_pages_over_limit: number | null;
  color_pages_over_limit: number | null;
}

interface SaleSummaryChartsProps {
  /** Contract term kind filter, shared with the table below. */
  settlementKind?: string;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The monthly sale summary, drawn. Reads the same endpoint the table below it
 * pages through, but always oldest-first and over a fixed window - a time axis
 * has to run forwards regardless of how the user has sorted the table.
 */
export default function SaleSummaryCharts({ settlementKind }: SaleSummaryChartsProps) {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const chartSx = useMemo(() => buildChartSx(palette), [palette]);

  const [rows, setRows] = useState<MonthlySaleSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${SETTLEMENTS_ENDPOINT}sale-summary-statistics/`, {
        params: {
          ordering: "-month",
          page_size: CHART_MONTHS,
          ...(settlementKind ? { settlement_kind: settlementKind } : {}),
        },
      });
      // Requested newest-first so the window is the most recent months, then
      // flipped, because a time axis reads left to right.
      setRows([...((response.data?.results as MonthlySaleSummaryRow[]) ?? [])].reverse());
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [settlementKind]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const monthKeys = useMemo(() => rows.map((row) => row.month), [rows]);
  const formatMonth = useCallback(
    (month: string, isTick: boolean) =>
      dayjs(month).format(isTick ? "MM.YY" : "MM.YYYY"),
    []
  );

  const monthAxis = useMemo(
    () => ({
      data: monthKeys,
      valueFormatter: (value: string, context: { location: string }) =>
        formatMonth(value, context.location === "tick"),
    }),
    [monthKeys, formatMonth]
  );

  const totals = useMemo(() => rows.map((row) => toNumber(row.total)), [rows]);
  /** The API nulls `total` for users without settlement.view_financial_data.
   * They get the page-usage charts but not the value one - an empty money
   * chart would read as "no sales" rather than "not yours to see". */
  const canSeeTotals = useMemo(
    () => rows.some((row) => row.total !== null && row.total !== undefined),
    [rows]
  );

  /** Page series, with the all-zero ones dropped so the legend only lists
   * things the chart actually draws (a fleet with no colour machines should
   * not carry a permanently flat "Color pages" line). */
  const pageSeries = useMemo(() => {
    const candidates = [
      { key: "black_pages_used" as const, label: t("Black pages"), slot: 0 },
      { key: "color_pages_used" as const, label: t("Color pages"), slot: 1 },
      { key: "scanned_pages_used" as const, label: t("Scanned pages"), slot: 2 },
    ];
    return candidates
      .map((candidate) => ({
        ...candidate,
        data: rows.map((row) => toNumber(row[candidate.key])),
      }))
      .filter((candidate) => candidate.data.some((value) => value !== 0));
  }, [rows, t]);

  const overLimitSeries = useMemo(() => {
    const candidates = [
      { key: "black_pages_over_limit" as const, label: t("Black pages over limit"), slot: 0 },
      { key: "color_pages_over_limit" as const, label: t("Color pages over limit"), slot: 1 },
    ];
    return candidates
      .map((candidate) => ({
        ...candidate,
        data: rows.map((row) => toNumber(row[candidate.key])),
      }))
      .filter((candidate) => candidate.data.some((value) => value !== 0));
  }, [rows, t]);

  const noRows = rows.length === 0;
  const pages = (value: number | null) =>
    value === null ? "" : formatCounterValue(value);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
      }}
    >
      {canSeeTotals && (
      <Box sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}>
        <ChartCard
          title={t("Monthly sale value")}
          subtitle={t("Confirmed settlements, last 24 months")}
          height={260}
          loading={loading}
          isEmpty={noRows || totals.every((value) => value === 0)}
        >
          {/* One measure, so one colour for every bar - shading bars by their
              own value would re-encode what their height already shows. */}
          <BarChart
            height={260}
            margin={{ left: 12, right: 20, top: 8, bottom: 8 }}
            grid={{ horizontal: true }}
            hideLegend
            borderRadius={4}
            xAxis={[{ scaleType: "band", label: t("Month"), height: 58, ...monthAxis }]}
            yAxis={[
              { width: 104, label: t("Value (zł)"), valueFormatter: formatAxisNumber },
            ]}
            series={[
              {
                data: totals,
                label: t("Value (zł)"),
                color: palette.series(0),
                valueFormatter: (value: number | null) =>
                  value === null ? "" : `${formatNumber(value)} zł`,
              },
            ]}
            sx={chartSx}
          />
        </ChartCard>
      </Box>
      )}

      <ChartCard
        title={t("Page usage over time")}
        subtitle={t("Pages billed per month")}
        height={260}
        loading={loading}
        isEmpty={noRows || pageSeries.length === 0}
      >
        {/* All three series are page counts, so they share one axis. */}
        <LineChart
          height={260}
          margin={{ left: 12, right: 20, top: 8, bottom: 8 }}
          grid={{ horizontal: true }}
          xAxis={[{ scaleType: "point", label: t("Month"), height: 58, ...monthAxis }]}
          yAxis={[
            { width: 96, label: t("Pages"), valueFormatter: formatAxisNumber },
          ]}
          series={pageSeries.map((series) => ({
            data: series.data,
            label: series.label,
            color: palette.series(series.slot),
            curve: "monotoneX",
            showMark: rows.length <= 24,
            valueFormatter: pages,
          }))}
          sx={{
            ...chartSx,
            "& .MuiLineElement-root": { strokeWidth: 2 },
            // A 2px surface ring keeps overlapping markers separable. No
            // transform here: SVG `scale` works about the coordinate origin,
            // not the mark's centre, and slides every dot off its line.
            "& .MuiMarkElement-root": {
              stroke: palette.surface,
              strokeWidth: 2,
            },
          }}
        />
      </ChartCard>

      <ChartCard
        title={t("Pages over limit")}
        subtitle={t("Usage billed beyond what the rent includes")}
        height={260}
        loading={loading}
        isEmpty={noRows || overLimitSeries.length === 0}
        emptyMessage={t("No usage over the included limits in this period.")}
      >
        <BarChart
          height={260}
          margin={{ left: 12, right: 20, top: 8, bottom: 8 }}
          grid={{ horizontal: true }}
          borderRadius={4}
          xAxis={[{ scaleType: "band", label: t("Month"), height: 58, ...monthAxis }]}
          yAxis={[
            { width: 96, label: t("Pages"), valueFormatter: formatAxisNumber },
          ]}
          series={overLimitSeries.map((series) => ({
            data: series.data,
            label: series.label,
            color: palette.series(series.slot),
            valueFormatter: pages,
          }))}
          sx={chartSx}
        />
      </ChartCard>
    </Box>
  );
}
